import { useEffect, useId, useRef, type ReactNode } from "react";
import { useI18n } from "../i18n";

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = "lg",
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  footer?: ReactNode;
  size?: "md" | "lg" | "xl";
  children: ReactNode;
}) {
  const { t } = useI18n();
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  // Focus moves to the dialog once, when it opens. Keeping this out of the
  // effect below matters: `onClose` is usually a fresh closure on every render,
  // so a combined effect re-focuses the close button after each keystroke and
  // a form inside the dialog can only ever receive one character.
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    // The dialog scrolls on its own; letting the page behind it scroll too is disorienting.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths: Record<string, string> = {
    md: "max-w-lg",
    lg: "max-w-3xl",
    xl: "max-w-6xl",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
      <div
        className="fixed inset-0 bg-slate-900/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative my-auto flex w-full ${widths[size]} max-h-[90vh] flex-col rounded-xl border border-slate-200 bg-white shadow-2xl`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-bold text-slate-800">
              {title}
            </h2>
            {description ? (
              <div className="mt-1 text-sm leading-relaxed text-slate-500">{description}</div>
            ) : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-lg text-slate-500 transition hover:bg-slate-100"
            aria-label={t("sysGuideClose")}
            title={t("sysGuideClose")}
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
